export function matchesSearch(text, keywords, query) {
  return `${text} ${keywords || ""}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
}

export function nextResizeValue(key, current, minimum, maximum, shiftKey = false) {
  const step = shiftKey ? 10 : 2;
  let next = current;
  if (key === "Home") next = minimum;
  if (key === "End") next = maximum;
  if (key === "ArrowLeft") next = current - step;
  if (key === "ArrowRight") next = current + step;
  return Math.min(maximum, Math.max(minimum, Math.round(next)));
}

export function formatFileSize(bytes) {
  const value = Math.max(0, Number(bytes) || 0);
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(value < 10 * 1024 ? 1 : 0)} KB`;
  return `${(value / 1024 ** 2).toFixed(value < 10 * 1024 ** 2 ? 1 : 0)} MB`;
}

export function matchesFileAccept(file, accept = "") {
  const rules = accept.split(",").map((rule) => rule.trim().toLocaleLowerCase()).filter(Boolean);
  if (!rules.length) return true;
  const name = String(file.name || "").toLocaleLowerCase();
  const type = String(file.type || "").toLocaleLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith(".")) return name.endsWith(rule);
    if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

export function mediaKindFromType(type = "", name = "") {
  const normalizedType = String(type || "").toLocaleLowerCase();
  const normalizedName = String(name || "").toLocaleLowerCase();
  if (normalizedType.startsWith("image/") || /\.(avif|gif|jpe?g|png|svg|webp)$/.test(normalizedName)) return "image";
  if (normalizedType.startsWith("video/") || /\.(m4v|mov|mp4|webm)$/.test(normalizedName)) return "video";
  if (normalizedType.startsWith("audio/") || /\.(aac|flac|m4a|mp3|ogg|wav)$/.test(normalizedName)) return "audio";
  if (normalizedType === "application/pdf" || /\.(docx?|odt|pdf|pptx?|txt|xlsx?)$/.test(normalizedName)) return "document";
  return "generic";
}

export function normalizeMediaProgress(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.min(100, Math.max(0, Math.round(numeric)));
}

export function normalizeAssetQuery(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

export function compareDataValues(first, second, direction = "ascending", type = "text") {
  const multiplier = direction === "descending" ? -1 : 1;
  if (type === "number") {
    const left = Number(first);
    const right = Number(second);
    const safeLeft = Number.isFinite(left) ? left : Number.NEGATIVE_INFINITY;
    const safeRight = Number.isFinite(right) ? right : Number.NEGATIVE_INFINITY;
    return (safeLeft - safeRight) * multiplier;
  }
  return String(first || "").localeCompare(String(second || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  }) * multiplier;
}

const interactionScopes = new WeakMap();

function createInteractionScope(root, name) {
  if (!root || typeof root.querySelectorAll !== "function") {
    throw new TypeError("Purple UI interactions require a DOM root");
  }
  let scopes = interactionScopes.get(root);
  if (!scopes) {
    scopes = new Map();
    interactionScopes.set(root, scopes);
  }
  scopes.get(name)?.destroy();
  const controller = new AbortController();
  const cleanups = new Set();
  let active = true;
  const scope = {
    get active() {
      return active;
    },
    signal: controller.signal,
    cleanup(callback) {
      cleanups.add(callback);
      return callback;
    },
    setTimeout(callback, delay = 0) {
      const timer = window.setTimeout(() => {
        cleanups.delete(cancel);
        if (active) callback();
      }, delay);
      const cancel = () => window.clearTimeout(timer);
      cleanups.add(cancel);
      return timer;
    },
    destroy() {
      if (!active) return;
      active = false;
      controller.abort();
      for (const cleanup of cleanups) cleanup();
      cleanups.clear();
      if (scopes.get(name) === scope) scopes.delete(name);
      if (!scopes.size) interactionScopes.delete(root);
    },
  };
  scopes.set(name, scope);
  return scope;
}

function listen(scope, target, type, listener, options = {}) {
  if (!target?.addEventListener) return;
  target.addEventListener(type, listener, { ...options, signal: scope.signal });
}

export function destroyInteractions(root = typeof document === "undefined" ? null : document) {
  const scopes = root && interactionScopes.get(root);
  if (!scopes) return;
  for (const scope of [...scopes.values()]) scope.destroy();
}

export function initTabs(root = document) {
  const scope = createInteractionScope(root, "tabs");
  for (const tabs of root.querySelectorAll("[data-p-tabs]")) {
    const tabList = tabs.querySelector('[role="tablist"]');
    const tabButtons = [...tabs.querySelectorAll('[role="tab"]')];
    const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];

    function selectTab(tab) {
      if (!tab || tab.getAttribute("aria-disabled") === "true") return;
      for (const button of tabButtons) {
        const selected = button === tab;
        button.setAttribute("aria-selected", String(selected));
        button.tabIndex = selected ? 0 : -1;
      }
      for (const panel of panels) panel.hidden = panel.id !== tab.getAttribute("aria-controls");
    }

    listen(scope, tabList, "click", (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab?.getAttribute("aria-disabled") !== "true") selectTab(tab);
    });

    listen(scope, tabList, "keydown", (event) => {
      const enabledTabs = tabButtons.filter((button) => button.getAttribute("aria-disabled") !== "true");
      const current = enabledTabs.indexOf(document.activeElement);
      const vertical = tabList.getAttribute("aria-orientation") === "vertical";
      const previousKey = vertical ? "ArrowUp" : "ArrowLeft";
      const nextKey = vertical ? "ArrowDown" : "ArrowRight";
      if (current < 0 || ![previousKey, nextKey, "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      let next = current;
      if (event.key === previousKey) next = (current - 1 + enabledTabs.length) % enabledTabs.length;
      if (event.key === nextKey) next = (current + 1) % enabledTabs.length;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = enabledTabs.length - 1;
      selectTab(enabledTabs[next]);
      enabledTabs[next].focus();
    });
  }
  return scope;
}

export function initPopovers(root = document) {
  const scope = createInteractionScope(root, "popovers");
  for (const trigger of root.querySelectorAll("[data-p-popover-trigger]")) {
    const popover = root.querySelector(`#${trigger.getAttribute("popovertarget")}`);
    if (!popover) continue;
    trigger.setAttribute("aria-expanded", "false");
    if (typeof popover.showPopover === "function") {
      listen(scope, popover, "toggle", (event) => trigger.setAttribute("aria-expanded", String(event.newState === "open")));
    } else {
      popover.hidden = true;
      const setOpen = (open) => {
        popover.hidden = !open;
        trigger.setAttribute("aria-expanded", String(open));
      };
      listen(scope, trigger, "click", () => setOpen(popover.hidden));
      listen(scope, document, "keydown", (event) => {
        if (event.key === "Escape" && !popover.hidden) {
          setOpen(false);
          trigger.focus();
        }
      });
      listen(scope, document, "pointerdown", (event) => {
        if (!popover.hidden && !popover.contains(event.target) && !trigger.contains(event.target)) setOpen(false);
      });
    }
  }
  return scope;
}

function menuItems(menu) {
  return [...menu.querySelectorAll('[role="menuitem"]')].filter((item) => item.getAttribute("aria-disabled") !== "true");
}

export function initDropdownMenus(root = document) {
  const scope = createInteractionScope(root, "dropdown-menus");
  for (const container of root.querySelectorAll("[data-p-menu]")) {
    const trigger = container.querySelector("[data-p-menu-trigger]");
    const menu = container.querySelector('[role="menu"]');
    if (!trigger || !menu) continue;
    for (const item of menu.querySelectorAll('[role="menuitem"]')) item.tabIndex = -1;

    const setOpen = (open, focus = null) => {
      menu.hidden = !open;
      trigger.setAttribute("aria-expanded", String(open));
      if (open && focus) focus.focus();
    };
    listen(scope, trigger, "click", () => setOpen(menu.hidden, menuItems(menu)[0]));
    listen(scope, trigger, "keydown", (event) => {
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
      event.preventDefault();
      const items = menuItems(menu);
      setOpen(true, event.key === "ArrowDown" ? items[0] : items.at(-1));
    });
    listen(scope, menu, "keydown", (event) => {
      const items = menuItems(menu);
      const current = items.indexOf(document.activeElement);
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        trigger.focus();
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      let next = current;
      if (event.key === "ArrowDown") next = (current + 1) % items.length;
      if (event.key === "ArrowUp") next = (current - 1 + items.length) % items.length;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = items.length - 1;
      items[next].focus();
    });
    listen(scope, menu, "click", (event) => {
      const item = event.target.closest('[role="menuitem"]');
      if (!item || item.getAttribute("aria-disabled") === "true") return;
      setOpen(false);
      trigger.focus();
      container.dispatchEvent(new CustomEvent("p-select", { bubbles: true, detail: { value: item.dataset.value } }));
    });
    listen(scope, container, "focusout", () => scope.setTimeout(() => {
      if (!container.contains(document.activeElement)) setOpen(false);
    }, 0));
    listen(scope, document, "pointerdown", (event) => {
      if (!container.contains(event.target)) setOpen(false);
    });
  }
  return scope;
}

export function initComboboxes(root = document) {
  const scope = createInteractionScope(root, "comboboxes");
  for (const container of root.querySelectorAll("[data-p-combobox]")) {
    const input = container.querySelector('[role="combobox"]');
    const listbox = container.querySelector('[role="listbox"]');
    const options = [...container.querySelectorAll('[role="option"]:not([data-p-combobox-empty])')];
    const empty = container.querySelector("[data-p-combobox-empty]");
    if (!input || !listbox) continue;
    for (const option of options) {
      option.tabIndex = -1;
      option.setAttribute("aria-selected", "false");
    }
    let activeIndex = -1;

    const visibleOptions = () => options.filter((option) => !option.hidden);
    const setOpen = (open) => {
      listbox.hidden = !open;
      input.setAttribute("aria-expanded", String(open));
      if (!open) {
        input.removeAttribute("aria-activedescendant");
        activeIndex = -1;
      }
    };
    const activate = (index) => {
      const visible = visibleOptions();
      if (!visible.length) return;
      activeIndex = (index + visible.length) % visible.length;
      for (const option of options) option.setAttribute("aria-selected", String(option === visible[activeIndex]));
      input.setAttribute("aria-activedescendant", visible[activeIndex].id);
      visible[activeIndex].scrollIntoView({ block: "nearest" });
    };
    const choose = (option) => {
      input.value = option.dataset.value || option.textContent.trim();
      for (const item of options) item.setAttribute("aria-selected", String(item === option));
      setOpen(false);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    };
    const filter = () => {
      const query = input.value.trim().toLocaleLowerCase();
      for (const option of options) option.hidden = !option.textContent.toLocaleLowerCase().includes(query);
      const visible = visibleOptions();
      if (empty) empty.hidden = visible.length > 0;
      setOpen(true);
      if (visible.length) activate(0);
      else {
        activeIndex = -1;
        input.removeAttribute("aria-activedescendant");
      }
    };

    listen(scope, input, "input", filter);
    listen(scope, input, "focus", () => {
      for (const option of options) option.hidden = false;
      if (empty) empty.hidden = true;
    });
    listen(scope, input, "keydown", (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;
      const visible = visibleOptions();
      if (event.key === "Enter" && !listbox.hidden && activeIndex >= 0) {
        event.preventDefault();
        choose(visible[activeIndex]);
        return;
      }
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
      event.preventDefault();
      if (listbox.hidden) setOpen(true);
      activate(activeIndex + (event.key === "ArrowDown" ? 1 : -1));
    });
    listen(scope, listbox, "click", (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) choose(option);
    });
    listen(scope, container, "focusout", () => scope.setTimeout(() => {
      if (!container.contains(document.activeElement)) setOpen(false);
    }, 0));
    listen(scope, document, "pointerdown", (event) => {
      if (!container.contains(event.target)) setOpen(false);
    });
  }
  return scope;
}

export function initCodeBlocks(root = document) {
  const scope = createInteractionScope(root, "code-blocks");
  for (const block of root.querySelectorAll("[data-p-code-block]")) {
    const button = block.querySelector("[data-p-copy-code]");
    const code = block.querySelector("code");
    if (!button || !code) continue;
    const defaultLabel = button.textContent.trim();
    listen(scope, button, "click", async () => {
      try {
        await navigator.clipboard.writeText(code.textContent);
        button.textContent = "Kopiert";
        button.dataset.pCopyState = "success";
      } catch {
        button.textContent = "Kopieren fehlgeschlagen";
        button.dataset.pCopyState = "error";
      }
      scope.setTimeout(() => {
        button.textContent = defaultLabel;
        delete button.dataset.pCopyState;
      }, 1800);
    });
  }
  return scope;
}

export function initTableOfContents(root = document) {
  const scope = createInteractionScope(root, "table-of-contents");
  if (!("IntersectionObserver" in window)) return scope;
  for (const toc of root.querySelectorAll("[data-p-toc]")) {
    const links = [...toc.querySelectorAll('a[href^="#"]')];
    const targets = links.map((link) => root.querySelector(link.hash)).filter(Boolean);
    if (!targets.length) continue;
    const observer = new IntersectionObserver((entries) => {
      const current = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!current) return;
      for (const link of links) link.toggleAttribute("aria-current", link.hash === `#${current.target.id}`);
      for (const link of links) if (link.hasAttribute("aria-current")) link.setAttribute("aria-current", "location");
    }, { rootMargin: "-15% 0px -70%", threshold: [0, 0.25, 0.6] });
    targets.forEach((target) => observer.observe(target));
    scope.cleanup(() => observer.disconnect());
  }
  return scope;
}

export function initDrawers(root = document) {
  const scope = createInteractionScope(root, "drawers");
  for (const trigger of root.querySelectorAll("[data-p-drawer-trigger]")) {
    const drawer = root.querySelector(`#${trigger.getAttribute("aria-controls")}`);
    if (!drawer) continue;
    trigger.setAttribute("aria-expanded", String(drawer.hasAttribute("open")));
    listen(scope, trigger, "click", () => {
      if (typeof drawer.showModal === "function") drawer.showModal();
      else drawer.setAttribute("open", "");
      trigger.setAttribute("aria-expanded", "true");
    });
    const closeDrawer = () => {
      if (typeof drawer.close === "function") drawer.close();
      else {
        drawer.removeAttribute("open");
        trigger.setAttribute("aria-expanded", "false");
        trigger.focus();
      }
    };
    for (const close of drawer.querySelectorAll("[data-p-drawer-close]")) {
      listen(scope, close, "click", closeDrawer);
    }
    listen(scope, drawer, "keydown", (event) => {
      if (event.key !== "Escape" || !drawer.hasAttribute("open")) return;
      event.preventDefault();
      closeDrawer();
    });
    listen(scope, drawer, "close", () => {
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus();
    });
  }
  return scope;
}

export function initCommandPalettes(root = document) {
  const scope = createInteractionScope(root, "command-palettes");
  for (const command of root.querySelectorAll("[data-p-command]")) {
    const trigger = [...root.querySelectorAll("[data-p-command-trigger]")].find((item) => item.getAttribute("aria-controls") === command.id);
    const input = command.querySelector('[role="combobox"]');
    const listbox = command.querySelector('[role="listbox"]');
    const options = [...command.querySelectorAll('[role="option"]')];
    const empty = command.querySelector("[data-p-command-empty]");
    if (!trigger || !input || !listbox) continue;
    let activeIndex = -1;
    const visibleOptions = () => options.filter((option) => !option.hidden && option.getAttribute("aria-disabled") !== "true");
    const activate = (index) => {
      const visible = visibleOptions();
      if (!visible.length) return;
      activeIndex = (index + visible.length) % visible.length;
      for (const option of options) {
        option.dataset.pActive = String(option === visible[activeIndex]);
        option.setAttribute("aria-selected", String(option === visible[activeIndex]));
      }
      input.setAttribute("aria-activedescendant", visible[activeIndex].id);
      visible[activeIndex].scrollIntoView({ block: "nearest" });
    };
    const close = () => {
      if (typeof command.close === "function") command.close();
      else command.removeAttribute("open");
    };
    const choose = (option) => {
      if (!option || option.getAttribute("aria-disabled") === "true") return;
      command.dispatchEvent(new CustomEvent("p-select", { bubbles: true, detail: { value: option.dataset.value } }));
      close();
    };
    const filter = () => {
      const query = input.value.trim().toLocaleLowerCase();
      for (const option of options) {
        option.hidden = !matchesSearch(option.textContent, option.dataset.keywords, query);
      }
      const visible = visibleOptions();
      if (empty) empty.hidden = visible.length > 0;
      activeIndex = -1;
      input.removeAttribute("aria-activedescendant");
      if (visible.length) activate(0);
    };
    const open = () => {
      input.value = "";
      activeIndex = -1;
      input.removeAttribute("aria-activedescendant");
      for (const option of options) {
        option.hidden = false;
        option.dataset.pActive = "false";
        option.setAttribute("aria-selected", "false");
      }
      if (empty) empty.hidden = true;
      if (typeof command.showModal === "function") command.showModal();
      else command.setAttribute("open", "");
      trigger.setAttribute("aria-expanded", "true");
      scope.setTimeout(() => input.focus(), 0);
    };
    trigger.setAttribute("aria-expanded", "false");
    listen(scope, trigger, "click", open);
    listen(scope, input, "input", filter);
    listen(scope, input, "keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "Enter") choose(visibleOptions()[activeIndex]);
      else activate(activeIndex + (event.key === "ArrowDown" ? 1 : -1));
    });
    listen(scope, listbox, "click", (event) => choose(event.target.closest('[role="option"]')));
    listen(scope, command.querySelector("[data-p-command-close]"), "click", close);
    listen(scope, command, "close", () => {
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus();
    });
    listen(scope, document, "keydown", (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLocaleLowerCase() !== "k") return;
      event.preventDefault();
      if (!command.hasAttribute("open")) open();
    });
  }
  return scope;
}

export function initResizablePanels(root = document) {
  const scope = createInteractionScope(root, "resizable-panels");
  for (const panels of root.querySelectorAll("[data-p-resizable]")) {
    const handle = panels.querySelector('[role="separator"]');
    if (!handle) continue;
    const minimum = Number(handle.getAttribute("aria-valuemin")) || 20;
    const maximum = Number(handle.getAttribute("aria-valuemax")) || 80;
    const initial = Number(handle.getAttribute("aria-valuenow")) || 42;
    let dragging = false;
    const update = (value) => {
      const next = Math.min(maximum, Math.max(minimum, Math.round(value)));
      panels.style.setProperty("--p-panel-start", `${next}%`);
      handle.setAttribute("aria-valuenow", String(next));
      handle.setAttribute("aria-valuetext", `${next}%`);
      panels.dispatchEvent(new CustomEvent("p-resize", { bubbles: true, detail: { value: next } }));
    };
    listen(scope, handle, "pointerdown", (event) => {
      dragging = true;
      handle.dataset.pDragging = "true";
      handle.setPointerCapture?.(event.pointerId);
    });
    listen(scope, handle, "pointermove", (event) => {
      if (!dragging) return;
      const bounds = panels.getBoundingClientRect();
      update(((event.clientX - bounds.left) / bounds.width) * 100);
    });
    const stopDragging = () => {
      dragging = false;
      delete handle.dataset.pDragging;
    };
    listen(scope, handle, "pointerup", stopDragging);
    listen(scope, handle, "pointercancel", stopDragging);
    listen(scope, handle, "keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const current = Number(handle.getAttribute("aria-valuenow")) || initial;
      update(nextResizeValue(event.key, current, minimum, maximum, event.shiftKey));
    });
    listen(scope, handle, "dblclick", () => update(initial));
    update(initial);
  }
  return scope;
}

export function initMultiSelects(root = document) {
  const scope = createInteractionScope(root, "multi-selects");
  for (const multi of root.querySelectorAll("[data-p-multiselect]")) {
    const input = multi.querySelector('[role="combobox"]');
    const listbox = multi.querySelector('[role="listbox"]');
    const options = [...multi.querySelectorAll('[role="option"]')];
    const chips = multi.querySelector("[data-p-multiselect-chips]");
    const empty = multi.querySelector("[data-p-multiselect-empty]");
    const native = multi.querySelector("[data-p-multiselect-native]");
    if (!input || !listbox || !chips) continue;
    const selected = new Set(options.filter((option) => option.getAttribute("aria-selected") === "true").map((option) => option.dataset.value));
    let activeIndex = -1;
    const visibleOptions = () => options.filter((option) => !option.hidden);
    const setOpen = (open) => {
      listbox.hidden = !open;
      input.setAttribute("aria-expanded", String(open));
      if (!open) {
        activeIndex = -1;
        input.removeAttribute("aria-activedescendant");
        for (const option of options) option.dataset.pActive = "false";
      }
    };
    const render = (emit = true) => {
      chips.replaceChildren();
      for (const value of selected) {
        const option = options.find((item) => item.dataset.value === value);
        if (!option) continue;
        const chip = document.createElement("span");
        chip.className = "p-chip";
        chip.append(document.createTextNode(option.textContent.trim()));
        const remove = document.createElement("button");
        remove.type = "button";
        remove.setAttribute("aria-label", `${option.textContent.trim()} entfernen`);
        remove.textContent = "×";
        listen(scope, remove, "click", () => {
          selected.delete(value);
          render();
          input.focus();
        });
        chip.append(remove);
        chips.append(chip);
      }
      for (const option of options) option.setAttribute("aria-selected", String(selected.has(option.dataset.value)));
      if (native) for (const option of native.options) option.selected = selected.has(option.value);
      if (emit) multi.dispatchEvent(new CustomEvent("p-change", { bubbles: true, detail: { values: [...selected] } }));
    };
    const activate = (index) => {
      const visible = visibleOptions();
      if (!visible.length) return;
      activeIndex = (index + visible.length) % visible.length;
      for (const option of options) option.dataset.pActive = String(option === visible[activeIndex]);
      input.setAttribute("aria-activedescendant", visible[activeIndex].id);
      visible[activeIndex].scrollIntoView({ block: "nearest" });
    };
    const toggle = (option) => {
      if (!option) return;
      if (selected.has(option.dataset.value)) selected.delete(option.dataset.value);
      else selected.add(option.dataset.value);
      render();
      input.value = "";
      for (const item of options) item.hidden = false;
      input.focus();
    };
    const filter = () => {
      const query = input.value.trim().toLocaleLowerCase();
      for (const option of options) {
        option.hidden = !matchesSearch(option.textContent, option.dataset.keywords, query);
      }
      const visible = visibleOptions();
      if (empty) empty.hidden = visible.length > 0;
      setOpen(true);
      if (visible.length) activate(0);
    };
    listen(scope, input, "focus", () => setOpen(true));
    listen(scope, input, "input", filter);
    listen(scope, input, "keydown", (event) => {
      if (event.key === "Escape") return setOpen(false);
      if (event.key === "Backspace" && !input.value && selected.size) {
        selected.delete([...selected].at(-1));
        render();
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "Enter") toggle(visibleOptions()[activeIndex]);
      else activate(activeIndex + (event.key === "ArrowDown" ? 1 : -1));
    });
    listen(scope, listbox, "click", (event) => toggle(event.target.closest('[role="option"]')));
    listen(scope, document, "pointerdown", (event) => {
      if (!multi.contains(event.target)) setOpen(false);
    });
    render(false);
  }
  return scope;
}

export function initFileUploads(root = document) {
  const scope = createInteractionScope(root, "file-uploads");
  for (const upload of root.querySelectorAll("[data-p-file-upload]")) {
    const input = upload.querySelector('input[type="file"]');
    const dropzone = upload.querySelector("[data-p-file-dropzone]");
    const list = upload.querySelector("[data-p-file-list]");
    const status = upload.querySelector("[data-p-file-status]");
    const clear = upload.querySelector("[data-p-file-clear]");
    if (!input || !dropzone || !list || !status) continue;
    const configuredMaximum = Number(upload.dataset.pMaxSize);
    const maximum = configuredMaximum > 0 ? configuredMaximum : Infinity;
    let activeFiles = [];

    const publish = (files, rejected = 0) => {
      activeFiles = [...files];
      list.replaceChildren();
      for (const file of activeFiles) {
        const item = document.createElement("li");
        const name = document.createElement("span");
        const size = document.createElement("small");
        name.textContent = file.name;
        size.textContent = formatFileSize(file.size);
        item.append(name, size);
        list.append(item);
      }
      list.hidden = activeFiles.length === 0;
      if (clear) clear.hidden = activeFiles.length === 0;
      const selectedText = activeFiles.length === 1 ? "1 Datei ausgewählt." : `${activeFiles.length} Dateien ausgewählt.`;
      status.textContent = rejected ? `${selectedText} ${rejected} nicht akzeptiert.` : selectedText;
      status.dataset.pState = rejected ? "warning" : "ready";
      upload.dispatchEvent(new CustomEvent("p-change", {
        bubbles: true,
        detail: { files: activeFiles.map(({ name, size, type }) => ({ name, size, type })), rejected },
      }));
    };

    const validate = (files) => {
      const candidates = input.multiple ? [...files] : [...files].slice(0, 1);
      const accepted = candidates.filter((file) => matchesFileAccept(file, input.accept) && file.size <= maximum);
      return { accepted, rejected: candidates.length - accepted.length };
    };

    const acceptFiles = (files, synchronize = false) => {
      const result = validate(files);
      if (synchronize && typeof DataTransfer === "function") {
        try {
          const transfer = new DataTransfer();
          result.accepted.forEach((file) => transfer.items.add(file));
          input.files = transfer.files;
        } catch {
          // Browsing remains native when a browser exposes but restricts DataTransfer.
        }
      }
      publish(result.accepted, result.rejected);
    };

    listen(scope, input, "change", () => acceptFiles(input.files, true));
    for (const eventName of ["dragenter", "dragover"]) {
      listen(scope, dropzone, eventName, (event) => {
        event.preventDefault();
        dropzone.dataset.pDragging = "true";
      });
    }
    for (const eventName of ["dragleave", "drop"]) {
      listen(scope, dropzone, eventName, (event) => {
        event.preventDefault();
        delete dropzone.dataset.pDragging;
      });
    }
    listen(scope, dropzone, "drop", (event) => acceptFiles(event.dataTransfer?.files || [], true));
    listen(scope, clear, "click", () => {
      input.value = "";
      publish([]);
      input.focus();
    });
  }
  return scope;
}

export function initMediaUploadQueues(root = document) {
  const scope = createInteractionScope(root, "media-upload-queues");
  for (const queue of root.querySelectorAll("[data-p-media-upload-queue]")) {
    const status = queue.querySelector("[data-p-media-upload-status]");
    listen(scope, queue, "click", (event) => {
      const button = event.target.closest("[data-p-media-action]");
      if (!button || button.disabled) return;
      const item = button.closest("[data-p-media-upload-item]");
      const action = button.dataset.pMediaAction;
      const itemId = item?.dataset.pMediaId || null;
      const name = item?.querySelector("[data-p-media-name]")?.textContent?.trim() || "Datei";
      queue.dispatchEvent(new CustomEvent("p-media-action", {
        bubbles: true,
        cancelable: true,
        detail: { action, itemId },
      }));
      if (status) status.textContent = `${action === "retry" ? "Erneut versuchen" : action === "cancel" ? "Abbrechen" : "Aktion"}: ${name}`;
    });
  }
  return scope;
}

function cropGeometry(cropper) {
  return Object.fromEntries(["x", "y", "width", "height", "zoom"].map((key) => {
    const input = cropper.querySelector("[data-p-crop-control=\"" + key + "\"]");
    return [key, Number(input?.value || (key === "zoom" ? 1 : 0))];
  }));
}

export function initImageCroppers(root = document) {
  const scope = createInteractionScope(root, "image-croppers");
  for (const cropper of root.querySelectorAll("[data-p-image-cropper]")) {
    const status = cropper.querySelector("[data-p-crop-status]");
    const publish = (type) => {
      const geometry = cropGeometry(cropper);
      cropper.style.setProperty("--p-crop-x", geometry.x + "%");
      cropper.style.setProperty("--p-crop-y", geometry.y + "%");
      cropper.style.setProperty("--p-crop-width", geometry.width + "%");
      cropper.style.setProperty("--p-crop-height", geometry.height + "%");
      if (status) status.textContent = "Ausschnitt " + geometry.width + " × " + geometry.height + " Prozent bei " + geometry.x + " / " + geometry.y;
      cropper.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { geometry } }));
    };
    listen(scope, cropper, "input", (event) => {
      if (event.target.matches("[data-p-crop-control]")) publish("p-crop-change");
    });
    listen(scope, cropper.querySelector("[data-p-crop-reset]"), "click", () => {
      cropper.querySelector("form")?.reset();
      publish("p-crop-reset");
    });
    listen(scope, cropper.querySelector("[data-p-crop-apply]"), "click", () => publish("p-crop-apply"));
    publish("p-crop-change");
  }
  return scope;
}

export function initLightboxes(root = document) {
  const scope = createInteractionScope(root, "lightboxes");
  for (const trigger of root.querySelectorAll("[data-p-lightbox-open]")) {
    listen(scope, trigger, "click", () => {
      const dialog = root.querySelector("#" + trigger.dataset.pLightboxOpen);
      if (!dialog || typeof dialog.showModal !== "function") return;
      dialog.showModal();
      dialog.dispatchEvent(new CustomEvent("p-lightbox-open", { bubbles: true, detail: { invoker: trigger } }));
    });
  }
  for (const dialog of root.querySelectorAll("[data-p-lightbox]")) {
    listen(scope, dialog.querySelector("[data-p-lightbox-close]"), "click", () => dialog.close());
    listen(scope, dialog, "close", () => dialog.dispatchEvent(new CustomEvent("p-lightbox-close", { bubbles: true })));
    listen(scope, dialog, "click", (event) => {
      const control = event.target.closest("[data-p-lightbox-navigate]");
      if (!control) return;
      dialog.dispatchEvent(new CustomEvent("p-lightbox-navigate", {
        bubbles: true,
        detail: { direction: control.dataset.pLightboxNavigate },
      }));
    });
  }
  return scope;
}

export function initAssetBrowsers(root = document) {
  const scope = createInteractionScope(root, "asset-browsers");
  for (const browser of root.querySelectorAll("[data-p-asset-browser]")) {
    const query = browser.querySelector("[data-p-asset-query]");
    const kind = browser.querySelector("[data-p-asset-kind]");
    const publishQuery = () => browser.dispatchEvent(new CustomEvent("p-asset-query", {
      bubbles: true,
      detail: { query: normalizeAssetQuery(query?.value), kind: kind?.value || "all" },
    }));
    listen(scope, query, "input", publishQuery);
    listen(scope, kind, "change", publishQuery);
    listen(scope, browser, "click", (event) => {
      const control = event.target.closest("[data-p-asset-select]");
      if (!control || control.disabled) return;
      const selected = control.getAttribute("aria-pressed") !== "true";
      control.setAttribute("aria-pressed", String(selected));
      browser.dispatchEvent(new CustomEvent("p-asset-select", {
        bubbles: true,
        detail: { assetId: control.dataset.pAssetId || null, selected },
      }));
    });
  }
  return scope;
}

export function initMetadataEditors(root = document) {
  const scope = createInteractionScope(root, "metadata-editors");
  for (const form of root.querySelectorAll("[data-p-metadata-editor]")) {
    const status = form.querySelector("[data-p-metadata-status]");
    listen(scope, form, "input", (event) => {
      form.dataset.pState = "dirty";
      if (status) status.textContent = "Ungespeicherte Änderungen";
      form.dispatchEvent(new CustomEvent("p-metadata-change", {
        bubbles: true,
        detail: { field: event.target.name || null, dirty: true },
      }));
    });
    listen(scope, form, "submit", (event) => {
      event.preventDefault();
      form.dispatchEvent(new CustomEvent("p-metadata-submit", {
        bubbles: true,
        cancelable: true,
        detail: { formData: new FormData(form) },
      }));
    });
  }
  return scope;
}

export function initMediaPickers(root = document) {
  const scope = createInteractionScope(root, "media-pickers");
  for (const picker of root.querySelectorAll("[data-p-media-picker]")) {
    const status = picker.querySelector("[data-p-media-picker-status]");
    listen(scope, picker, "click", (event) => {
      const control = event.target.closest("[data-p-media-pick]");
      if (!control || control.disabled) return;
      const multiple = picker.dataset.pMediaPickerMultiple === "true";
      const selected = control.getAttribute("aria-pressed") !== "true";
      if (!multiple) for (const item of picker.querySelectorAll("[data-p-media-pick]")) item.setAttribute("aria-pressed", "false");
      control.setAttribute("aria-pressed", String(selected));
      const label = control.querySelector("strong")?.textContent?.trim() || "Asset";
      if (status) status.textContent = selected ? `${label} ausgewählt` : `${label} abgewählt`;
      picker.dispatchEvent(new CustomEvent("p-media-pick", {
        bubbles: true,
        detail: { assetId: control.dataset.pMediaId || null, selected, multiple },
      }));
    });
  }
  return scope;
}

export function initAnnotationHotspots(root = document) {
  const scope = createInteractionScope(root, "annotation-hotspots");
  for (const hotspot of root.querySelectorAll("[data-p-annotation-hotspot]")) {
    const status = hotspot.querySelector("[data-p-annotation-status]");
    listen(scope, hotspot, "click", (event) => {
      const control = event.target.closest("[data-p-annotation-select]");
      if (!control || control.disabled) return;
      for (const item of hotspot.querySelectorAll("[data-p-annotation-select]")) item.setAttribute("aria-pressed", "false");
      control.setAttribute("aria-pressed", "true");
      hotspot.dataset.pState = "selected";
      const label = control.dataset.pAnnotationLabel || control.textContent?.trim() || "Annotation";
      if (status) status.textContent = `${label} ausgewählt`;
      hotspot.dispatchEvent(new CustomEvent("p-annotation-select", {
        bubbles: true,
        detail: { annotationId: control.dataset.pAnnotationId || null, label },
      }));
    });
  }
  return scope;
}

export function initDataTables(root = document) {
  const scope = createInteractionScope(root, "data-tables");
  for (const workspace of root.querySelectorAll("[data-p-data-workspace]")) {
    const table = workspace.querySelector("[data-p-data-table]");
    const body = table?.tBodies[0];
    if (!table || !body) continue;
    const filter = workspace.querySelector("[data-p-data-filter]");
    const selectionStatus = workspace.querySelector("[data-p-selection-status]");
    const mobileSort = workspace.querySelector("[data-p-data-sort]");
    const selectAllControls = [...workspace.querySelectorAll("[data-p-select-all]")];
    const rows = () => [...body.querySelectorAll("tr[data-p-row-id]")];
    const rowCheckbox = (row) => row.querySelector("[data-p-row-select]");

    const publishSelection = () => {
      const available = rows().filter((row) => !row.hidden);
      const selected = rows().filter((row) => rowCheckbox(row)?.checked);
      const visibleSelected = available.filter((row) => rowCheckbox(row)?.checked);
      for (const row of rows()) row.dataset.pSelected = String(Boolean(rowCheckbox(row)?.checked));
      for (const control of selectAllControls) {
        control.checked = available.length > 0 && visibleSelected.length === available.length;
        control.indeterminate = visibleSelected.length > 0 && visibleSelected.length < available.length;
      }
      if (selectionStatus) selectionStatus.textContent = selected.length === 1 ? "1 Zeile ausgewählt" : `${selected.length} Zeilen ausgewählt`;
      workspace.dispatchEvent(new CustomEvent("p-selection-change", {
        bubbles: true,
        detail: { selected: selected.map((row) => row.dataset.pRowId) },
      }));
    };

    const applyFilter = () => {
      const query = filter?.value || "";
      for (const row of rows()) row.hidden = !matchesSearch(row.textContent, row.dataset.pKeywords, query);
      publishSelection();
    };

    for (const control of selectAllControls) {
      listen(scope, control, "change", () => {
        for (const row of rows().filter((item) => !item.hidden)) {
          const checkbox = rowCheckbox(row);
          if (checkbox && !checkbox.disabled) checkbox.checked = control.checked;
        }
        publishSelection();
      });
    }
    listen(scope, body, "change", (event) => {
      if (event.target.matches("[data-p-row-select]")) publishSelection();
    });
    listen(scope, filter, "input", applyFilter);

    const sortRows = (key, direction, type = "text") => {
      for (const item of table.querySelectorAll("th[aria-sort]")) item.setAttribute("aria-sort", "none");
      const header = table.querySelector(`[data-p-sort-key="${key}"][data-p-sort]`)?.closest("th");
      header?.setAttribute("aria-sort", direction);
      const ordered = rows().sort((first, second) => {
        const firstValue = first.querySelector(`[data-p-sort-key="${key}"]`)?.dataset.pSortValue;
        const secondValue = second.querySelector(`[data-p-sort-key="${key}"]`)?.dataset.pSortValue;
        return compareDataValues(firstValue, secondValue, direction, type);
      });
      for (const row of ordered) body.append(row);
      workspace.dispatchEvent(new CustomEvent("p-sort", { bubbles: true, detail: { key, direction } }));
    };

    const sortButtons = [...table.querySelectorAll("[data-p-sort]")];
    for (const sortButton of sortButtons) {
      listen(scope, sortButton, "click", () => {
        const header = sortButton.closest("th");
        const direction = header.getAttribute("aria-sort") === "ascending" ? "descending" : "ascending";
        sortRows(sortButton.dataset.pSortKey, direction, sortButton.dataset.pSortType);
        if (mobileSort) mobileSort.value = `${sortButton.dataset.pSortKey}:${direction}`;
      });
    }
    listen(scope, mobileSort, "change", () => {
      const [key, direction] = mobileSort.value.split(":");
      sortRows(key, direction, mobileSort.selectedOptions[0]?.dataset.pSortType);
    });
    const stackedTable = table.closest('[data-p-mobile="stack"]');
    const compactQuery = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 42rem)") : null;
    const updateSortFocus = () => {
      for (const sortButton of sortButtons) sortButton.tabIndex = stackedTable && compactQuery?.matches ? -1 : 0;
    };
    listen(scope, compactQuery, "change", updateSortFocus);
    updateSortFocus();

    const setEditing = (row, editing) => {
      row.dataset.pEditing = String(editing);
      for (const display of row.querySelectorAll("[data-p-display]")) display.hidden = editing;
      for (const editor of row.querySelectorAll("[data-p-editor]")) editor.hidden = !editing;
      row.querySelector("[data-p-edit]")?.toggleAttribute("hidden", editing);
      row.querySelector("[data-p-save]")?.toggleAttribute("hidden", !editing);
      row.querySelector("[data-p-cancel]")?.toggleAttribute("hidden", !editing);
    };

    const cancelEdit = (row, returnFocus = true) => {
      for (const editor of row.querySelectorAll("[data-p-editor]")) {
        editor.value = editor.dataset.pInitialValue ?? "";
        editor.removeAttribute("aria-invalid");
      }
      for (const error of row.querySelectorAll("[data-p-edit-error]")) error.hidden = true;
      setEditing(row, false);
      if (returnFocus) row.querySelector("[data-p-edit]")?.focus();
    };

    listen(scope, body, "click", (event) => {
      const action = event.target.closest("[data-p-edit], [data-p-save], [data-p-cancel]");
      const row = action?.closest("tr[data-p-row-id]");
      if (!action || !row) return;
      if (action.matches("[data-p-edit]")) {
        for (const other of rows().filter((item) => item !== row && item.dataset.pEditing === "true")) cancelEdit(other, false);
        for (const editor of row.querySelectorAll("[data-p-editor]")) editor.dataset.pInitialValue = editor.value;
        setEditing(row, true);
        row.querySelector("[data-p-editor]")?.focus();
        return;
      }
      if (action.matches("[data-p-cancel]")) {
        cancelEdit(row);
        return;
      }
      const editors = [...row.querySelectorAll("[data-p-editor]")];
      const invalid = editors.find((editor) => !editor.checkValidity());
      for (const editor of editors) editor.setAttribute("aria-invalid", String(!editor.checkValidity()));
      for (const error of row.querySelectorAll("[data-p-edit-error]")) {
        const owner = editors.find((editor) => editor.getAttribute("aria-describedby")?.split(/\s+/).includes(error.id));
        error.hidden = !owner || owner.checkValidity();
      }
      if (invalid) {
        invalid.focus();
        return;
      }
      const values = {};
      for (const editor of editors) {
        const field = editor.dataset.pField;
        const display = row.querySelector(`[data-p-display="${field}"]`);
        const value = editor.value.trim();
        editor.value = value;
        values[field] = value;
        if (display) display.textContent = editor.matches("select") ? editor.selectedOptions[0]?.textContent : value;
        const cell = editor.closest("[data-p-sort-value]");
        if (cell) cell.dataset.pSortValue = value;
        editor.removeAttribute("aria-invalid");
      }
      for (const error of row.querySelectorAll("[data-p-edit-error]")) error.hidden = true;
      setEditing(row, false);
      applyFilter();
      workspace.dispatchEvent(new CustomEvent("p-edit", {
        bubbles: true,
        detail: { rowId: row.dataset.pRowId, values },
      }));
      if (row.hidden) filter?.focus();
      else row.querySelector("[data-p-edit]")?.focus();
    });

    listen(scope, body, "keydown", (event) => {
      const row = event.target.closest('tr[data-p-editing="true"]');
      if (event.key !== "Escape" || !row) return;
      event.preventDefault();
      cancelEdit(row);
    });
    applyFilter();
  }
  return scope;
}

export function initInteractions(root = typeof document === "undefined" ? null : document) {
  if (!root) return { active: false, destroy() {} };
  const handles = [
    initTabs(root),
    initPopovers(root),
    initDropdownMenus(root),
    initComboboxes(root),
    initCodeBlocks(root),
    initTableOfContents(root),
    initDrawers(root),
    initCommandPalettes(root),
    initResizablePanels(root),
    initMultiSelects(root),
    initFileUploads(root),
    initMediaUploadQueues(root),
    initImageCroppers(root),
    initLightboxes(root),
    initAssetBrowsers(root),
    initMetadataEditors(root),
    initMediaPickers(root),
    initAnnotationHotspots(root),
    initDataTables(root),
  ];
  let active = true;
  return {
    get active() {
      return active && handles.some((handle) => handle.active);
    },
    destroy() {
      if (!active) return;
      active = false;
      for (const handle of handles) handle.destroy();
    },
  };
}

export function showToast({ title, message, duration = 4200, actionLabel, onAction, onDismiss, dismissible = false, compact = false, maxVisible = 3, region = document.querySelector("[data-p-toast-region]") }) {
  if (!region) throw new Error("Purple UI toast region is missing");
  const limit = Math.max(1, Math.floor(Number(maxVisible) || 3));
  const visible = [...region.querySelectorAll(".p-toast")];
  for (const stale of visible.slice(0, Math.max(0, visible.length - limit + 1))) {
    stale.dispatchEvent(new CustomEvent("p-toast-dismiss", { detail: { reason: "overflow" } }));
  }
  const toast = document.createElement("div");
  toast.className = `p-toast${compact ? " p-toast--compact" : ""}`;
  toast.setAttribute("role", "status");
  const heading = document.createElement("strong");
  heading.textContent = title;
  const copy = document.createElement("p");
  copy.textContent = message;
  toast.append(heading, copy);
  const actions = document.createElement("div");
  actions.className = "p-toast__actions";
  let timer = null;
  let dismissed = false;
  const dismiss = (reason = "close") => {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    toast.dispatchEvent(new CustomEvent("p-dismiss", { bubbles: true, detail: { reason } }));
    toast.remove();
    onDismiss?.(reason);
  };
  toast.addEventListener("p-toast-dismiss", (event) => dismiss(event.detail?.reason));
  if (actionLabel) {
    const action = document.createElement("button");
    action.type = "button";
    action.className = "p-button p-button--sm p-button--soft";
    action.textContent = actionLabel;
    action.addEventListener("click", () => {
      try { onAction?.(); } finally { dismiss("action"); }
    });
    actions.append(action);
  }
  if (dismissible || duration <= 0) {
    const close = document.createElement("button");
    close.type = "button";
    close.className = "p-button p-button--sm p-button--ghost";
    close.setAttribute("aria-label", `${title} schließen`);
    close.textContent = "Schließen";
    close.addEventListener("click", () => dismiss("close"));
    actions.append(close);
  }
  if (actions.children.length) toast.append(actions);
  region.append(toast);
  timer = duration > 0 ? window.setTimeout(() => dismiss("timeout"), duration) : null;
  return toast;
}
